import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
	intelligenceApi,
	type IntelligencePeriodPreset,
} from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { intelligenceReportHospitalId } from "@/hooks/useChainReportHospitalScope";
import {
	CommandCentreFilters,
	type IntelligencePeriodFilterValue,
} from "@/components/intelligence/CommandCentreFilters";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import { ManagementAttentionList } from "@/components/intelligence/ManagementAttentionSection";
import { IntelligenceExportButton } from "@/components/intelligence/IntelligenceExportButton";
import { IntelligenceServerExportButton } from "@/components/intelligence/IntelligenceServerExportButton";
import { buildAttentionExportRows } from "@/lib/intelligenceExport";
import { Card, CardContent } from "@/components/ui/card";

export default function ManagementAttentionPage() {
	const { user, activeHospitalId, isChainAdmin } = useAuth();
	const isSuperAdmin = user?.role === "SUPER_ADMIN";
	const isInvestor = user?.role === "INVESTOR";
	const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
	const showHospitalFilter = isSuperAdmin || isChainAdmin || investorHasChainScope;
	const chainHospitals = user?.hospitals ?? [];
	const isChainScoped = isChainAdmin || investorHasChainScope;

	const defaultFinancialHospitalId = useMemo(
		() =>
			defaultFinancialReportHospitalId({
				isSuperAdmin,
				isChainScoped,
				activeHospitalId,
				fallbackHospitalId: chainHospitals[0]?.id ?? null,
			}),
		[isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
	);

	const [period, setPeriod] = useState<IntelligencePeriodFilterValue>({ preset: "this_month" });
	const [selectedHospitalId, setSelectedHospitalId] = useState("");

	useEffect(() => {
		if (!selectedHospitalId && defaultFinancialHospitalId !== undefined) {
			setSelectedHospitalId(defaultFinancialHospitalId || "all");
		}
	}, [defaultFinancialHospitalId, selectedHospitalId]);

	const { data: superHospitals } = useQuery({
		queryKey: ["super-admin-hospitals-attention"],
		queryFn: () => superAdminApi.getHospitals(),
		enabled: isSuperAdmin,
	});

	const hospitalOptions = useMemo(() => {
		if (isSuperAdmin && superHospitals?.length) {
			return superHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (isChainScoped && chainHospitals.length) {
			return chainHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (user?.hospitalId) {
			const name = chainHospitals.find((h) => h.id === user.hospitalId)?.name ?? "My Hospital";
			return [{ id: user.hospitalId, name }];
		}
		return [];
	}, [isSuperAdmin, isChainScoped, superHospitals, chainHospitals, user?.hospitalId]);

	const reportHospitalId = useMemo(
		() =>
			intelligenceReportHospitalId({
				showHospitalFilter,
				selectedHospitalId,
				isChainScoped,
			}),
		[showHospitalFilter, selectedHospitalId, isChainScoped]
	);

	const customReady =
		period.preset !== "custom" || (!!period.from && !!period.to && period.from <= period.to);

	const queryParams = {
		period: period.preset as IntelligencePeriodPreset,
		from: period.preset === "custom" ? period.from : undefined,
		to: period.preset === "custom" ? period.to : undefined,
		hospitalId: reportHospitalId,
		compare: true,
	};

	const { data, isLoading, isError, error } = useQuery({
		queryKey: ["intelligence-attention", queryParams],
		queryFn: () => intelligenceApi.getAttention(queryParams),
		enabled: customReady,
	});

	const basePath = isSuperAdmin ? "/super-admin" : "/hospital-admin";

	return (
		<div className="space-y-6 pb-10">
			<IntelligenceSubNav basePath={basePath} />

			<div>
				<h1 className="text-2xl font-bold tracking-tight text-slate-900">Management Attention</h1>
				<p className="text-sm text-muted-foreground mt-1">
					Deterministic rule-based alerts · {data?.message ?? "—"}
				</p>
			</div>

			<CommandCentreFilters
				hospitals={hospitalOptions}
				showHospitalFilter={showHospitalFilter}
				hospitalId={selectedHospitalId}
				onHospitalChange={setSelectedHospitalId}
				period={period}
				onPeriodChange={setPeriod}
			/>

			{data && customReady && (
				<div className="flex justify-end gap-2">
					<IntelligenceExportButton
						filename={`management-attention-${period.preset}`}
						buildRows={() =>
							buildAttentionExportRows(data, period.preset === "custom" ? `${period.from}-${period.to}` : period.preset)
						}
						label="Quick CSV"
					/>
					<IntelligenceServerExportButton
						report="attention"
						params={queryParams}
						filename={`management-attention-${period.preset}`}
					/>
				</div>
			)}

			{isLoading && customReady && (
				<div className="flex justify-center py-16 text-muted-foreground gap-2">
					<Loader2 className="h-5 w-5 animate-spin" /> Loading…
				</div>
			)}

			{isError && customReady && (
				<Card className="border-destructive/40">
					<CardContent className="pt-4 text-sm text-destructive flex gap-2">
						<AlertTriangle className="h-4 w-4 shrink-0" />
						{(error as Error)?.message ?? "Failed to load management attention"}
					</CardContent>
				</Card>
			)}

			{data && customReady && (
				<ManagementAttentionList data={data} basePath={basePath} />
			)}
		</div>
	);
}
